"use client";

import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@shared-utils/ui";
import { Tooltip, TooltipContent } from "./tooltip";
import { TooltipTrigger } from "@radix-ui/react-tooltip";

const basicButtonStyle =
  " disabled:pointer-events-none  cursor-pointer shadow-sm border border-foreground/10" as const;
const colorButton = " text-background dark:text-foreground" as const;

const buttonVariants = cva(
  "inline-flex font-normal items-center justify-center rounded-sm text-sm transition-colors duration-100 sgt focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring cursor-pointer disabled:cursor-default ",
  {
    variants: {
      variant: {
        default:
          "bg-primary hover:bg-primary/90 focus-visible:bg-primary/90 disabled:bg-primary/70 disabled:text-primary-foreground/60 text-primary-foreground " +
          basicButtonStyle,
        secondary:
          basicButtonStyle +
          " bg-secondary hover:bg-secondary/50 focus-visible:bg-secondary/50 border border-border disabled:text-foreground/40 text-foreground ",
        white:
          basicButtonStyle +
          " bg-background hover:bg-background/50 focus-visible:bg-background/50 border border-border disabled:text-foreground/40 text-foreground ",
        background:
          "bg-background hover:bg-background/50 focus-visible:bg-secondary/50 border border-border disabled:text-foreground/40 " +
          basicButtonStyle,
        destructive:
          "bg-tl-red-500 hover:bg-tl-red-400" + basicButtonStyle + colorButton,
        outline:
          "border border-input bg-background shadow-xs hover:bg-accent hover:text-foreground",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:text-white",
        text: "hover:opacity-60 px-0",
        textDestructive:
          "hover:opacity-60 text-destructive mx-0 px-1 font-medium",
        icon: "hover:bg-accent hover:text-accent-foreground rounded-full dark:hover:text-white",
        iconOnly:
          "hover:opacity-60 hover:bg-transparent bg-transparent text-inherit disabled:opacity-40 dark:hover:text-white",

        // Terteriary Colors
        green:
          "bg-ter-green-500 hover:bg-ter-green-400 text-background dark:text-foreground" +
          basicButtonStyle,
        blue:
          "bg-ter-blue-500 hover:bg-ter-blue-400" +
          basicButtonStyle +
          colorButton,
        bluegray:
          "bg-ter-bluegray-500 hover:bg-ter-bluegray-400" +
          basicButtonStyle +
          colorButton,
        gray:
          "bg-ter-gray-500 hover:bg-ter-gray-400" +
          basicButtonStyle +
          colorButton,
        yellow:
          "bg-ter-yellow-500 hover:bg-ter-yellow-400" +
          basicButtonStyle +
          colorButton,
        purple:
          "bg-ter-purple-500 hover:bg-ter-purple-400" +
          basicButtonStyle +
          colorButton,
        cyan:
          "bg-ter-cyan-500 hover:bg-ter-cyan-400" +
          basicButtonStyle +
          colorButton,
        mint:
          "bg-ter-mint-500 hover:bg-ter-mint-400" +
          basicButtonStyle +
          colorButton,

        // Trafic Light Colors
        tlGreen:
          "bg-tl-green-500 hover:bg-tl-green-400" +
          basicButtonStyle +
          colorButton,
        tlYellow:
          "bg-tl-yellow-500 hover:bg-tl-yellow-400" +
          basicButtonStyle +
          colorButton,
        tlRed:
          "bg-tl-red-500 hover:bg-tl-red-400" + basicButtonStyle + colorButton,
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        xs: "h-6 rounded-md px-2 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9 border-none shadow-none ",
        iconSmall: "h-7 w-7 border-none shadow-none dark:hover:text-white",
        text: "size-xs text-inherit h-auto px-0 py-0 disabled:opacity-40 font-inherit!",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
    compoundVariants: [
      {
        variant: "ghost",
        size: "iconSmall",
        className:
          "rounded-full dark:hover:bg-ter-blue-200 dark:focus-visible:bg-ter-blue-200",
      },
    ],
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  disabledTooltipContent?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      disabledTooltipContent,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const button = (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );

    if (props.disabled && disabledTooltipContent) {
      return (
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>
            <span className="inline-block">{button}</span>
          </TooltipTrigger>
          <TooltipContent>{disabledTooltipContent}</TooltipContent>
        </Tooltip>
      );
    } else {
      return button;
    }
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
